import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { type CardDispute, CardDisputeWorkflow } from '@column/api';
import { Button, Dropdown, Icon, formatNumber } from '@column/column-ui-kit';
import {
  Badge,
  DataTable,
  type DataTableColumnDef,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Timestamp,
} from '@column/ui';

import { FilterDropdown } from '~/app/pages/Transfers/Overview';
import { ROUTE } from '~/app/routes';
import { Loading, Pagination } from '~/components';
import { LogoLoading } from '~/elements';
import { useQueryParams } from '~/hooks/useQueryParams';
import { useCanManageCardDisputes, useCardDisputes } from '~/lib/api/cardDisputes';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import {
  CARD_DISPUTE_STAGES,
  CARD_DISPUTE_STAGE_LABELS,
  CARD_DISPUTE_SUBMISSION_STATUSES,
  CARD_DISPUTE_SUBMISSION_STATUS_LABELS,
  type CardDisputeStage,
  type CardDisputeSubmissionStatus,
  formatRespondBy,
  getDisputeReasonLabel,
  getDisputeStageBadgeClasses,
  getDisputeStageLabel,
  getDisputeStatusBadgeClasses,
  getDisputeStatusLabel,
  getSubmissionStatusBadgeClasses,
  getSubmissionStatusLabel,
  getWorkflowLabel,
} from '~/lib/disputes/labels';
import { useNavigate } from '~/lib/navigation';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { PAGE_SIZE_OPTIONS, useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner } from '~/styles';
import { formatDateTime, getFormattedDate } from '~/util';

import { IssuerDashboardGate } from './IssuerDashboardGate';

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 0 var(--page-margin) 12px;
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ResetFilter = styled(Button)`
  padding-left: 0;
  padding-right: 0;
`;

const DisputeFilterDropdown = styled(FilterDropdown)`
  min-width: 0;
`;

const STAGE_OPTIONS = CARD_DISPUTE_STAGES.map((stage) => ({
  value: stage,
  label: CARD_DISPUTE_STAGE_LABELS[stage],
}));

const SUBMISSION_STATUS_OPTIONS = CARD_DISPUTE_SUBMISSION_STATUSES.map((submissionStatus) => ({
  value: submissionStatus,
  label: CARD_DISPUTE_SUBMISSION_STATUS_LABELS[submissionStatus],
}));

type StageFilter = '' | CardDisputeStage;
type SubmissionStatusFilter = '' | CardDisputeSubmissionStatus;

interface CardDisputesFilters {
  stage: StageFilter;
  submission_status: SubmissionStatusFilter;
}

interface CardDisputesFilterUrlParams {
  stage?: string;
  submission_status?: string;
  starting_after?: string;
  page_index?: string;
}

const EMPTY_FILTERS: CardDisputesFilters = { stage: '', submission_status: '' };

const isStageFilter = (value: string): value is StageFilter =>
  value === '' || STAGE_OPTIONS.some((option) => option.value === value);

const isSubmissionStatusFilter = (value: string): value is SubmissionStatusFilter =>
  value === '' || SUBMISSION_STATUS_OPTIONS.some((option) => option.value === value);

const filtersFromUrl = (params: CardDisputesFilterUrlParams | undefined): CardDisputesFilters => {
  if (!params) return EMPTY_FILTERS;

  return {
    stage: params.stage && isStageFilter(params.stage) ? params.stage : '',
    submission_status:
      params.submission_status && isSubmissionStatusFilter(params.submission_status) ? params.submission_status : '',
  };
};

const filtersToUrl = (filters: CardDisputesFilters): CardDisputesFilterUrlParams => {
  const params: CardDisputesFilterUrlParams = {};
  if (filters.stage) params.stage = filters.stage;
  if (filters.submission_status) params.submission_status = filters.submission_status;
  return params;
};

const WORKFLOW_BADGE_CLASSES: Record<CardDisputeWorkflow, string> = {
  [CardDisputeWorkflow.COLLABORATION]:
    'border-blue-700/10 bg-blue-200/40 text-blue-700 dark:border-blue-400/20 dark:bg-blue-500/15 dark:text-blue-300',
  [CardDisputeWorkflow.ALLOCATION]:
    'border-cyan-700/10 bg-cyan-100/50 text-cyan-700 dark:border-cyan-400/20 dark:bg-cyan-500/15 dark:text-cyan-300',
};

const PageCardDisputesContent: React.FC = () => {
  const navigate = useNavigate();
  const { pageSize, timeFormat, update: updateTableConfig } = useTableConfig('card-disputes');
  const { addDangerNotification } = useNotificationStore();
  const { currentPlatform, isSandbox } = useSessionStore();
  const canManage = useCanManageCardDisputes();
  const { queryParams, setQueryParams } = useQueryParams<CardDisputesFilterUrlParams>();
  const initialPagination = useRef({
    cursor: queryParams?.starting_after ? String(queryParams.starting_after) : undefined,
    pageIndex: Number(queryParams?.page_index) || 0,
  });
  const {
    cursor,
    onNext,
    onPrevious,
    reset: resetPagination,
    setPageSize,
    pageIndex,
    hasPrevious,
  } = usePaginationState({
    defaultPageSize: pageSize,
    initialCursor: initialPagination.current.cursor,
    initialPageIndex: initialPagination.current.pageIndex,
  });

  const [filters, setFilters] = useState<CardDisputesFilters>(() => filtersFromUrl(queryParams));
  const [entriesOpen, setEntriesOpen] = useState(false);

  useEffect(() => {
    const base = filtersToUrl(filters);
    setQueryParams(() => ({
      ...base,
      ...(cursor ? { starting_after: cursor } : {}),
      ...(pageIndex > 0 ? { page_index: String(pageIndex) } : {}),
    }));
  }, [filters, cursor, pageIndex, setQueryParams]);

  const updateFilter = <K extends keyof CardDisputesFilters>(key: K, value: CardDisputesFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleFilterReset = () => {
    setFilters(EMPTY_FILTERS);
  };

  const isFilterActive = Boolean(filters.stage || filters.submission_status);

  const isInitialFilterRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialFilterRenderRef.current) {
      isInitialFilterRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [filters, resetPagination]);

  const isInitialPlatformRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialPlatformRenderRef.current) {
      isInitialPlatformRenderRef.current = false;
      return;
    }
    // Switching platform or live/sandbox mode must not keep the previous context's cursor.
    resetPagination();
  }, [currentPlatform?.id, isSandbox, resetPagination]);

  const {
    data: response,
    isError,
    isLoading,
    isFetching,
    refetch,
  } = useCardDisputes({
    query: {
      stage: filters.stage || undefined,
      submission_status: filters.submission_status || undefined,
    },
    cursor,
    pageSize,
  });

  const cardDisputes = useMemo(() => response?.card_disputes ?? [], [response]);
  const hasMore = response?.has_more ?? false;
  const activePageSize = Number(pageSize);
  const tableMinHeight = cardDisputes.length === 0 ? 341 : undefined;

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    updateTableConfig({ limit: value }).catch(() => {
      addDangerNotification({ content: 'Failed to save page size preference' });
    });
  };

  const handleTimeOptionChange = (value: TableTimeFormat | undefined) => {
    if (!value) return;
    updateTableConfig({ timeFormat: value }).catch(() => {
      addDangerNotification({ content: 'Failed to save time format preference' });
    });
  };

  const handlePageNext = () => {
    if (cardDisputes.length > 0) {
      onNext({ cursor: cardDisputes[cardDisputes.length - 1].id });
    }
  };

  const columns: DataTableColumnDef<CardDispute>[] = [
    {
      accessor: 'id',
      header: 'Dispute ID',
      width: 'minmax(200px, 1.5fr)',
      className: 'min-w-0',
      cell: ({ row }) => <span className="min-w-0 truncate font-mono text-xs">{row.id}</span>,
    },
    {
      accessor: 'card_transaction_id',
      header: 'Transaction',
      width: 'minmax(220px, 1.5fr)',
      className: 'min-w-0',
      cell: ({ row }) => (
        <a
          className="min-w-0 truncate font-mono text-xs text-primary underline-offset-4 hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${ROUTE.CARD_TRANSACTIONS}/edit/${row.card_transaction_id}`);
          }}
        >
          {row.card_transaction_id}
        </a>
      ),
    },
    {
      accessor: 'reason',
      header: 'Reason',
      width: 'minmax(min-content, 180px)',
      className: 'min-w-0',
      cell: ({ row }) => <span className="truncate">{getDisputeReasonLabel(row.reason)}</span>,
    },
    {
      accessor: 'workflow',
      header: 'Workflow',
      width: 'minmax(min-content, 130px)',
      cell: ({ row }) => (
        <Badge variant="outline" className={WORKFLOW_BADGE_CLASSES[row.workflow]}>
          {getWorkflowLabel(row.workflow)}
        </Badge>
      ),
    },
    {
      accessor: 'stage',
      header: 'Stage',
      width: 'minmax(min-content, 170px)',
      cell: ({ row }) => (
        <Badge variant="outline" className={getDisputeStageBadgeClasses(row.stage)}>
          {getDisputeStageLabel(row.stage)}
        </Badge>
      ),
    },
    {
      accessor: 'status',
      header: 'Status',
      width: 'minmax(min-content, 140px)',
      cell: ({ row }) => (
        <Badge variant="outline" className={getDisputeStatusBadgeClasses(row.status)}>
          {getDisputeStatusLabel(row.status)}
        </Badge>
      ),
    },
    {
      accessor: 'amount',
      header: 'Amount',
      width: 'min-content',
      cell: ({ row }) => <span>{formatNumber(row.amount, row.currency)}</span>,
    },
    {
      accessor: 'submission_status',
      header: 'Submission',
      width: 'minmax(min-content, 160px)',
      cell: ({ row }) => (
        <Badge variant="outline" className={getSubmissionStatusBadgeClasses(row.submission_status)}>
          {getSubmissionStatusLabel(row.submission_status)}
        </Badge>
      ),
    },
    {
      accessor: 'respond_by',
      header: 'Respond By',
      width: 'min-content',
      cell: ({ row }) => <span className="whitespace-nowrap">{formatRespondBy(row.respond_by)}</span>,
    },
    {
      accessor: 'created_at',
      header: (
        <Dropdown
          size="small"
          positionRight
          active={timeFormat}
          options={[
            { label: 'Relative Time', value: TableTimeFormat.Relative },
            { label: 'Date / Time', value: TableTimeFormat.Absolute },
          ]}
          onChange={handleTimeOptionChange}
          customLabel={
            <Flex alignItems="center" gap={4}>
              Created <Icon.ChevronDown />
            </Flex>
          }
        />
      ),
      width: 'min-content',
      cell: ({ row }) => (
        <Timestamp
          date={row.created_at}
          align="right"
          className="text-xs text-muted-foreground"
          formatHover={(hoverDate) =>
            formatDateTime(hoverDate, {
              timeZoneFormat: timeFormat === TableTimeFormat.Relative ? 'local' : 'utc',
            })
          }
        >
          {timeFormat === TableTimeFormat.Relative
            ? getFormattedDate(row.created_at, 'time-passed')
            : formatDateTime(row.created_at, { timeZoneFormat: 'local' })}
        </Timestamp>
      ),
    },
  ];

  return (
    <Inner pt={0} px="var(--page-margin)">
      <Toolbar>
        <ToolbarGroup>
          <DisputeFilterDropdown
            size="small"
            active={filters.stage}
            $isFilterActive={Boolean(filters.stage)}
            options={STAGE_OPTIONS}
            label={STAGE_OPTIONS.find((option) => option.value === filters.stage)?.label ?? 'Stage'}
            onChange={(value: StageFilter) => updateFilter('stage', value)}
          />
          <DisputeFilterDropdown
            size="small"
            active={filters.submission_status}
            $isFilterActive={Boolean(filters.submission_status)}
            options={SUBMISSION_STATUS_OPTIONS}
            label={
              SUBMISSION_STATUS_OPTIONS.find((option) => option.value === filters.submission_status)?.label ??
              'Submission'
            }
            onChange={(value: SubmissionStatusFilter) => updateFilter('submission_status', value)}
          />
          {isFilterActive && (
            <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
              Reset
            </ResetFilter>
          )}
        </ToolbarGroup>
        <ToolbarGroup>
          {canManage && (
            <Button
              size="small"
              variant="primary"
              icon={<Icon.Plus />}
              // Part 04 owns the transaction entry point; until it lands, send managers to their
              // card transactions to pick a settled transaction.
              onClick={() => navigate(ROUTE.CARD_TRANSACTIONS)}
            >
              New dispute
            </Button>
          )}
        </ToolbarGroup>
      </Toolbar>

      <Loading isLoading={isLoading}>
        <DataTable<CardDispute>
          columns={columns}
          data={cardDisputes}
          rowKey={(row) => row.id}
          isLoading={isFetching}
          loadingContent={<LogoLoading />}
          style={{ minHeight: tableMinHeight }}
          emptyContent={
            isError ? (
              <Empty className="min-h-[300px]">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Icon.Warning />
                  </EmptyMedia>
                  <EmptyTitle>Error loading disputes</EmptyTitle>
                  <EmptyDescription>Something went wrong loading disputes. Try again.</EmptyDescription>
                  <Button
                    size="small"
                    variant="secondary"
                    onClick={() => {
                      refetch().catch(() => {
                        /* the error state remains visible */
                      });
                    }}
                  >
                    Retry
                  </Button>
                </EmptyHeader>
              </Empty>
            ) : (
              <Empty className="min-h-[300px]">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Icon.Shield />
                  </EmptyMedia>
                  <EmptyTitle>No disputes found</EmptyTitle>
                  <EmptyDescription>
                    {isFilterActive
                      ? 'No disputes were found matching the specified filters'
                      : 'Disputes filed on card transactions will appear here.'}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            )
          }
          rowClassName="min-h-11"
          onRowClick={(row) => {
            navigate(`${ROUTE.CARD_DISPUTES}/edit/${row.id}`);
          }}
        />

        <div className="relative z-[1] flex items-center justify-between pt-4">
          <Dropdown
            options={PAGE_SIZE_OPTIONS.map((entries: number) => ({
              label: entries.toString(),
              value: entries,
            }))}
            size="small"
            variant="muted"
            active={activePageSize}
            customLabel={
              <Badge
                variant="secondary"
                className="rounded-[8px] [&>svg]:ml-[-4px] [&>svg]:inline-block [&>svg]:align-top"
              >
                {activePageSize}
                {entriesOpen ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
              </Badge>
            }
            onOpenChange={setEntriesOpen}
            onChange={handlePageSizeChange}
          />
          <Pagination
            current={pageIndex + 1}
            disablePrev={!hasPrevious}
            disableNext={!hasMore}
            onPrev={onPrevious}
            onNext={handlePageNext}
          />
        </div>
      </Loading>
    </Inner>
  );
};

export const PageCardDisputes: React.FC = () => (
  <IssuerDashboardGate>
    <PageCardDisputesContent />
  </IssuerDashboardGate>
);
